import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import './_styles_/ClearingHouseCreateTab.css';
import ExpandedListTab from '../ExpandedListTab';
import useExtendedMutation from '../../hooks/useExtendedMutation';
import { ALL_CLEARING_HOUSES_QUERY_NAME, CREATE_CLEARING_HOUSE } from '../../graphql/clearingHouses';
import SaveButton from '../buttons/SaveButton';
import TextField from '../fields/TextField';
import useExtendedState from '../../hooks/useExtendedState';
import SelectField from '../fields/SelectField';
import { clearingHousePropertiesType } from '../../types';
import useMultiValidator from '../../hooks/useMultiValidator';
import DefaultCashLimitsTable from './DefaultCashLimitsTable';
import {
    CLEARING_HOUSE_CODE_MAX_LETTERS,
    CLEARING_HOUSE_NAME_MAX_LETTERS,
    codeValidator,
    nameValidator,
} from '../../validators/clearingHouses';
import { compositeValidator, wafValidator } from '../../hooks/validationSupport';

ClearingHouseCreateTab.propTypes = {
    hideHandler: PropTypes.func.isRequired,
    clearingHouseProps: clearingHousePropertiesType.isRequired,
};

function ClearingHouseCreateTab(props) {
    const {
        hideHandler,
        clearingHouseProps,
    } = props;
    const {
        availableCurrencies,
        availableTimeZones,
        minCashLimit,
        maxCashLimit,
    } = clearingHouseProps;
    const { t } = useTranslation();
    const memoizedTimeZonesMap = useMemo(() => (
        availableTimeZones.map((availableTimeZone) => (
            <option value={availableTimeZone.ID} key={availableTimeZone.ID}>{availableTimeZone.ID}</option>
        ))
    ), [availableTimeZones]);
    const nameState = useExtendedState('');
    const codeState = useExtendedState('');
    const timeZoneState = useExtendedState(availableTimeZones[0].ID);
    const defaultCashLimitsState = useExtendedState(availableCurrencies.map((availableCurrency) => ({
        currencyName: availableCurrency.name,
        currencyCode: availableCurrency.code,
        cashLimit: 0.00,
        isValid: true,
    })));
    const onDefaultCashLimitChange = (currencyCode, cashLimit, isValidX) => {
        const newDefaultCashLimits = defaultCashLimitsState.value.map((defaultCashLimit) => {
            let result = defaultCashLimit;
            if (defaultCashLimit.currencyCode === currencyCode) {
                result = {
                    currencyName: defaultCashLimit.currencyName,
                    currencyCode,
                    cashLimit,
                    isValid: isValidX,
                };
            }
            return result;
        });
        defaultCashLimitsState.setValue(newDefaultCashLimits);
    };
    const { validators, isValid } = useMultiValidator(false, {
        name: compositeValidator(nameValidator(t), wafValidator(t)),
        code: compositeValidator(codeValidator(t), wafValidator(t)),
    });
    const [isDisabled, setIsDisabled] = useState(true);
    const initialValidation = !defaultCashLimitsState.value.every((defaultCashLimit) => defaultCashLimit.isValid) ? Promise.reject() : Promise.resolve();
    Promise.all([initialValidation, isValid()])
        .then(() => setIsDisabled(false))
        .catch(() => setIsDisabled(true));

    const extendedMutation = useExtendedMutation(CREATE_CLEARING_HOUSE, {
        variables: {
            name: nameState.value,
            code: codeState.value,
            timeZone: timeZoneState.value,
            cashLimits: defaultCashLimitsState.value.map((defaultCashLimit) => ({
                currencyCode: defaultCashLimit.currencyCode,
                cashLimit: defaultCashLimit.cashLimit,
            })),
        },
        successCallback: hideHandler,
        successMessageOptions: { key: 'clearing-houses.notification.clearing-house-create-success', dataProperty: 'createClearingHouse' },
        failureMessageOptions: { key: 'clearing-houses.notification.clearing-house-create-failure', params: { name: nameState.value, code: codeState.value } },
        refetchQueries: [ALL_CLEARING_HOUSES_QUERY_NAME],
    });
    return (
        <ExpandedListTab
            className="clearing-house-create-tab"
            title={t('clearing-houses.clearing-house-detail')}
        >
            <div className="rich-list-row-expanded--fields">
                <TextField
                    label={t('clearing-houses.name')}
                    state={nameState}
                    validator={validators.name}
                    maxLength={CLEARING_HOUSE_NAME_MAX_LETTERS}
                    className="clearing-house-create-tab--fields--name wide"
                    placeholder={t('clearing-houses.placeholder.name')}
                    isRequired
                />
                <TextField
                    label={t('clearing-houses.code')}
                    state={codeState}
                    validator={validators.code}
                    maxLength={CLEARING_HOUSE_CODE_MAX_LETTERS}
                    className="clearing-house-create-tab--fields--code"
                    placeholder={t('clearing-houses.placeholder.code')}
                    isRequired
                />
                <SelectField
                    label={t('clearing-houses.time-zone')}
                    state={timeZoneState}
                    options={memoizedTimeZonesMap}
                    className="clearing-house-create-tab--fields--time-zone"
                    inputId="clearing-house-create-tab--fields--time-zone"
                    isRequired
                />
            </div>
            <div className="clearing-house-create-tab--fields--default-cash-limits">
                <DefaultCashLimitsTable
                    minCashLimit={minCashLimit}
                    maxCashLimit={maxCashLimit}
                    defaultCashLimits={defaultCashLimitsState.value}
                    onDefaultCashLimitChange={onDefaultCashLimitChange}
                    isChanged={defaultCashLimitsState.isChanged}
                    onReset={defaultCashLimitsState.reset}
                />
            </div>
            <div className="rich-list-row-expanded--buttons">
                <SaveButton
                    extendedMutation={extendedMutation}
                    disabled={isDisabled}
                    confirmationMessage={t('clearing-houses.confirm-create-clearing-house', { name: nameState.value, code: codeState.value })}
                />
            </div>
        </ExpandedListTab>
    );
}

export default ClearingHouseCreateTab;
